import { create } from 'zustand';
import type { AuditCertificateVO } from '@/services/manageService/auditCertificateService';
import { querySelectionList } from '@/services/manageService/auditCertificateService';

const EMPTY_CERT: AuditCertificateVO = {
  id: 0,
  auditorEnglishName: undefined,
  accountingFirmName: undefined,
  accountingFirmChineseName: undefined,
  auditorSignName: undefined,
  pcNumber: undefined,
  auditorAddress: undefined,
  auditorChineseAddress: undefined,
  auditorFaxNumber: undefined,
  auditorTelRepNumber: undefined,
  auditorTelBankConfNumber: undefined,
  auditorBrNumber: undefined,
  certificateUrl: undefined,
  isValid: undefined,
  isDel: undefined,
};

const DEFAULT_TTL_MS = 5 * 60 * 1000;

let inflightAllCert: Promise<AuditCertificateVO[]> | null = null;

interface CertificateState {
  all: AuditCertificateVO[];
  loading: boolean;
  loaded: boolean;
  lastFetchedAt: number | null;
  cleanAll: () => void;
  loadAllCert: (options?: { force?: boolean; ttlMs?: number }) => Promise<AuditCertificateVO[]>;
  refreshAllCert: () => Promise<AuditCertificateVO[]>;
  getCertById: (id: number) => Promise<AuditCertificateVO>;
}

export const useCertificateStore = create<CertificateState>((set, get) => ({
  all: [],
  loading: false,
  loaded: false,
  lastFetchedAt: null,

  cleanAll: () => set({ all: [], loaded: false, lastFetchedAt: null }),

  loadAllCert: async (options) => {
    const s = get();
    const now = Date.now();
    const ttl = typeof options?.ttlMs === 'number' ? options!.ttlMs! : DEFAULT_TTL_MS;

    const hasCache = Array.isArray(s.all) && s.all.length > 0;
    const notExpired = typeof s.lastFetchedAt === 'number' && (now - (s.lastFetchedAt as number)) < ttl;

    if (!options?.force && hasCache && notExpired) return s.all;

    if (inflightAllCert) return inflightAllCert;

    set({ loading: true });
    inflightAllCert = (async () => {
      try {
        const list = await querySelectionList();
        const arr = Array.isArray(list) ? list : [];
        set({ all: arr, loaded: true, lastFetchedAt: now, loading: false });
        return arr;
      } catch {
        set({ loading: false });
        if (hasCache) return s.all;
        set({ all: [], loaded: false, lastFetchedAt: null });
        return [];
      } finally {
        inflightAllCert = null;
      }
    })();

    return inflightAllCert;
  },

  refreshAllCert: async () => {
    return get().loadAllCert({ force: true, ttlMs: 0 });
  },

  getCertById: async (id: number) => {
    const s = get();
    const numId = Number(id);
    let found = (s.all || []).find((c) => Number(c?.id) === numId);
    if (found) return found;

    const refreshed = await get().loadAllCert();
    found = (refreshed || []).find((c) => Number(c?.id) === numId);
    return found ?? EMPTY_CERT;
  },
}));
